import React from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/mobile/lib/sendAnalyticsEvent/index';
import {getButton} from '@optimaxdev/design-system';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeatureWithAnalytics} from 'components/feature';
import {CART_BUTTON} from 'constants/analytics/events/cart';
import {REFERER_FIND} from 'constants/analytics/mentionMe';
import {CL_COUPONS_TO_HIDE} from 'constants/contactLenses';
import {SafeRender} from 'desktop/components/helpers/safeRender/safeRender';
import {MentionMe} from 'desktop/components/mentionMe/mentionMe';
import {ApplicationStoreType} from 'desktop/store';
import {flags, isUhcEnabled} from 'libs/rollout';

import {BannerWrapperConnected} from '../../features/bannerTemplate/components/bannerWrapper/bannerWrapper';
import {CartBanner} from '../../features/bannerTemplate/components/cartBanner/cartBanner';
import {isFullLensesCart} from '../../store/selectors';
import {getQuoteSpecialOffers} from '../../store/selectors/quoteInfo/quoteInfo';
import {getSpecialOfferBannerContentType} from '../../store/selectors/specialOffer/specialOffer';
import {CartCoupon} from '../cartCoupon/cartCoupon';
import {SpecialOffer} from '../specialOffer/specialOffer';

import {CartSpecialOfferContentPropType, PropsType} from './cartSpecialOfferType';

import s from './cartSpecialOffer.scss';

const sendAnalytic = () =>
    sendDesktopAnalyticsEvent(CART_BUTTON, {
        action: 'Right Section',
        label: 'Referral Program - Clicked',
    });

/**
 * Renders special offers applied on cart items
 *
 * @param {*} {specialOfferData} - details about the special offers
 * @returns {React.ReactElement} - react element
 */
export const CartSpecialOffers: React.FC<PropsType> = React.memo(
    ({bannerContentType, isFullLenses, specialOfferData, specialOffers}): React.ReactElement => {
        const buttonProps = getButton({onClick: sendAnalytic, onKeyDown: sendAnalytic});
        const isDCartUICouponBox = useFeatureWithAnalytics('dCartUI_CouponBox', false, 'desktop');
        const suggestedCoupon = specialOffers?.[0].coupon;
        const shouldShowCLCoupons = !(
            flags.autoApplyCL.getValue() === 'Variation B' &&
            CL_COUPONS_TO_HIDE.includes(suggestedCoupon ?? '')
        );

        return (
            <>
                <div
                    className={classNames(s.contentWrapper, {
                        [s.dCartCouponBox]: isDCartUICouponBox,
                    })}
                >
                    {shouldShowCLCoupons && (
                        <SafeRender>
                            {bannerContentType === 'html' ? (
                                <SpecialOffer specialOffer={specialOfferData} />
                            ) : (
                                bannerContentType === 'json' && (
                                    <BannerWrapperConnected
                                        component={CartBanner}
                                        json={specialOfferData}
                                    />
                                )
                            )}
                        </SafeRender>
                    )}
                    <CartCoupon />
                </div>
                {!isFullLenses && !isDCartUICouponBox && (
                    <div {...buttonProps} className={classNames(s.referer)} role="none">
                        <MentionMe
                            params={{
                                implementation: 'link',
                                locale: 'en_GB',
                                situation: 'dashboard',
                            }}
                            integratingType={REFERER_FIND}
                        />
                    </div>
                )}
            </>
        );
    },
);

CartSpecialOffers.displayName = 'CartSpecialOffers';

/**
 *  mapStateToProps
 */
const mapStateToProps = (store: ApplicationStoreType) => {
    return {
        appliedCoupons: store.cart.totals.appliedCoupons,
        bannerContentType: getSpecialOfferBannerContentType(store),
        isFullLenses: isFullLensesCart(store),
        specialOffers: getQuoteSpecialOffers(store),
    };
};

const CartSpecialOfferConnected = connect(mapStateToProps)(CartSpecialOffers);

/**
 * Renders special offers applied on cart items
 *
 * @param {CartSpecialOfferContentPropType} props - props types
 * @returns {React.ReactElement} - react element
 */
export const CartSpecialOfferContent: React.FC<CartSpecialOfferContentPropType> = ({
    specialOfferData,
}) => {
    if (isUhcEnabled()) {
        return null;
    }

    return <CartSpecialOfferConnected specialOfferData={specialOfferData} />;
};
